import React, { useState } from "react";
import { Pencil, Check, X } from "lucide-react";
import { NarrativeBlock } from "@/pages/ReportView";

/**
 * EditableNarrative
 *
 * Click pencil to swap a NarrativeBlock for a textarea. Save calls back with the
 * new text + optional short edit-note; Cancel restores the original.
 *
 * Props:
 *  - text: string
 *  - serif: bool
 *  - onSave: ({ text, edit_note }) => Promise<void>
 *  - testidPrefix: 'doctor' | 'patient'
 */
export default function EditableNarrative({ text, serif, onSave, testidPrefix, readOnly = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text || "");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const start = () => {
    setDraft(text || "");
    setNote("");
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setDraft(text || "");
    setNote("");
  };

  const save = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    try {
      await onSave({ text: draft, edit_note: note });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="relative group">
        {!readOnly && (
          <button
            data-testid={`${testidPrefix}-narrative-edit-button`}
            onClick={start}
            className="no-print absolute -top-2 right-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity bg-white"
            style={{ borderColor: "var(--color-border)", color: "var(--color-text-secondary)" }}
          >
            <Pencil className="w-3.5 h-3.5" /> Edit
          </button>
        )}
        <NarrativeBlock text={text} serif={serif} />
      </div>
    );
  }

  return (
    <div className="no-print">
      <textarea
        data-testid={`${testidPrefix}-narrative-edit-textarea`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={Math.max(12, Math.min(30, (draft.split("\n").length || 1) + 2))}
        className={`w-full border rounded-md p-4 focus:outline-none focus:ring-2 focus:ring-[#2A4736] ${serif ? "font-serif-report text-[16px] leading-[1.7]" : "font-body text-[14px] leading-[1.65]"}`}
        style={{ borderColor: "var(--color-border)", background: "var(--bg-canvas)" }}
      />
      <input
        data-testid={`${testidPrefix}-narrative-edit-note`}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        maxLength={200}
        placeholder="Short reason for this edit (optional)"
        className="mt-3 w-full border bg-white rounded-md px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2A4736]"
        style={{ borderColor: "var(--color-border)" }}
      />
      <div className="mt-3 flex items-center gap-2">
        <button
          data-testid={`${testidPrefix}-narrative-edit-save`}
          onClick={save}
          disabled={saving || !draft.trim()}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-white font-medium text-sm disabled:opacity-60"
          style={{ background: "var(--color-primary)" }}
        >
          <Check className="w-4 h-4" /> {saving ? "Saving…" : "Save edits"}
        </button>
        <button
          data-testid={`${testidPrefix}-narrative-edit-cancel`}
          onClick={cancel}
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border font-medium text-sm hover:bg-white"
          style={{ borderColor: "var(--color-border)" }}
        >
          <X className="w-4 h-4" /> Cancel
        </button>
      </div>
    </div>
  );
}
